import React, { useState } from 'react'
import { CgCheckO, CgChevronDown } from 'react-icons/cg'

import { useUpdateOneTimeProductMutation } from 'modules/ProductInfo/productInfoApi'
import { useReadyForSaleTasks } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Switch } from 'common/components/Switch/Switch'
import { Tooltip } from 'common/components/Tooltip/Tooltip'

export function ReadyForSaleSection({ coachOrgId, productId, product }) {
  const { createAlert } = useAlert()
  const [updateOneTimeProduct] = useUpdateOneTimeProductMutation()
  const [isUpdating, setIsUpdating] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isRowHovered, setIsRowHovered] = useState(false)

  const {
    incompleteTasks,
    isLoading,
    isReadyForSale: readyForSale,
  } = useReadyForSaleTasks({
    coachOrgId,
    productId,
    product,
  })
  const toggleDisabled = isLoading || isUpdating || (!readyForSale && incompleteTasks.length > 0)

  const handleToggle = async () => {
    if (toggleDisabled) {
      return
    }
    setIsUpdating(true)
    await updateOneTimeProduct({ coachOrgId, productId, product: { readyForSale: !readyForSale } })
    createAlert({
      text: !readyForSale ? 'Product is ready for sale!' : 'Product is no longer for sale',
      type: 'success',
    })
    setIsUpdating(false)
  }

  const hasIncompleteTasks = !isLoading && incompleteTasks.length > 0

  const incompleteList = (
    <ul>
      {incompleteTasks.map((task) => (
        <li key={task.id} className='flex items-center mb-2 last:mb-0'>
          <CgCheckO className='w-[18px] h-[18px] text-gray-400 mr-2.5 flex-shrink-0' />
          <span className='text-sm text-gray-600'>{task.label}</span>
        </li>
      ))}
    </ul>
  )

  // The shared tooltip centers its text, which ragged-centers a bulleted list
  const tooltipContent = (
    <ul className='space-y-1 text-left'>
      {incompleteTasks.map((task) => (
        <li key={task.id}>• {task.label}</li>
      ))}
    </ul>
  )

  return (
    <div className='mt-6 -mb-6 max-w-2xl bg-white rounded-xl border-2 border-gray-200 px-5 py-4'>
      <div className='flex items-center justify-between'>
        <span className='font-semibold text-tBlack'>Ready for sale</span>
        <div className={`relative ${toggleDisabled ? 'opacity-60' : ''}`}>
          <Switch name='readyForSale' isChecked={readyForSale} onChange={handleToggle} />
          {toggleDisabled && !readyForSale && !isLoading && (
            <Tooltip
              content='Complete the remaining steps to enable'
              triggerClasses='absolute inset-0 z-[5] cursor-not-allowed'
            />
          )}
        </div>
      </div>
      <p className='text-sm text-gray-500 mt-1'>Make this product visible and purchasable on your storefront.</p>
      {hasIncompleteTasks && (
        <div className='mt-3 pt-3 border-t border-gray-200'>
          <button
            type='button'
            onClick={() => setIsExpanded((prev) => !prev)}
            onMouseEnter={() => setIsRowHovered(true)}
            onMouseLeave={() => setIsRowHovered(false)}
            className='flex items-center justify-between w-full group'
          >
            <Tooltip
              content={tooltipContent}
              open={!isExpanded && isRowHovered}
              triggerClasses='text-sm font-medium text-gray-600 group-hover:text-tBlack'
            >
              {incompleteTasks.length} {incompleteTasks.length === 1 ? 'step' : 'steps'} left to complete
            </Tooltip>
            <CgChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
          {isExpanded && <div className='mt-3'>{incompleteList}</div>}
        </div>
      )}
    </div>
  )
}
